import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiArrowLeft, FiDownload, FiFile, FiTrash2 } from "react-icons/fi";
import { transfersApi } from "../../services/transfersApi";
import CopyLink from "../../components/share/CopyLink";
import { KIND_ICONS, formatBytes } from "../../components/share/fileKinds";
import { useExpiryLabel } from "./ShareDashboard";

/** One transfer for its owner: link, files (incl. what clients uploaded to a request link), downloads. */
export default function ShareTransferDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const expiryLabel = useExpiryLabel();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    transfersApi
      .get(id)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(t("share.dashboard.delete_confirm", { subject: data.transfer.subject }))) return;
    await transfersApi.remove(id).catch(() => {});
    navigate("/transfers");
  };

  if (notFound) {
    return (
      <div className="shr-card shr-empty">
        <p className="shr-muted">{t("share.public.not_found_title")}</p>
        <Link to="/transfers" className="shr-btn ghost mt-3">{t("share.detail.back")}</Link>
      </div>
    );
  }
  if (!data) return <div className="shr-center"><div className="shr-spinner" /></div>;

  const { transfer, files } = data;
  const active = ["open", "ready"].includes(transfer.status) && (!transfer.expires_at || new Date(transfer.expires_at) > Date.now());

  return (
    <>
      <Link to="/transfers" className="shr-muted d-inline-flex align-items-center gap-1 mb-3" style={{ textDecoration: "none" }}>
        <FiArrowLeft /> {t("share.detail.back")}
      </Link>

      <div className="shr-card">
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div style={{ minWidth: 0 }}>
            <span className={`shr-badge ${transfer.kind}`}>{t(`share.dashboard.kind_${transfer.kind}`)}</span>
            <h1 className="shr-title mt-2">{transfer.subject}</h1>
            <p className="shr-muted">
              {t("share.dashboard.files", { count: transfer.file_count })} · {formatBytes(transfer.total_bytes)} · {expiryLabel(transfer.expires_at)}
            </p>
          </div>
          <button type="button" className="shr-btn danger-text" onClick={handleDelete}>
            <FiTrash2 /> {t("share.dashboard.delete")}
          </button>
        </div>

        {transfer.message && <p className="shr-public-message mt-3">{transfer.message}</p>}

        {active && (
          <div className="mt-3">
            <CopyLink url={transfer.link} shareTitle={transfer.subject} />
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-4">
          <span className="shr-label mb-0">{t("share.detail.files_title")}</span>
          {files.length > 0 && active && (
            <a className="shr-btn dark sm" href={transfersApi.ownerZipUrl(transfer.transfer_id)}>
              <FiDownload /> {t("share.public.download_all")}
            </a>
          )}
        </div>

        {files.length === 0 ? (
          <p className="shr-muted mt-3">
            {transfer.kind === "request" ? t("share.detail.no_files_request") : t("share.detail.no_files")}
          </p>
        ) : (
          <ul className="shr-files">
            {files.map((f) => {
              const Icon = KIND_ICONS[f.category] || FiFile;
              return (
                <li key={f.file_id} className="shr-file">
                  <span className={`shr-file-icon ${f.category}`}><Icon size={16} /></span>
                  <div className="shr-file-body">
                    <div className="shr-file-name" title={f.name}>{f.name}</div>
                    <div className="shr-file-meta">
                      {formatBytes(f.size)}
                      {f.uploader_name && ` · ${t("share.detail.from", { name: f.uploader_name })}`}
                    </div>
                  </div>
                  {active && (
                    <a className="shr-icon-btn" href={transfersApi.ownerFileUrl(transfer.transfer_id, f.file_id)} aria-label={t("share.public.download")}>
                      <FiDownload size={15} />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}
